import { useEffect, useRef, useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import type { VideoMetadata } from '@/types/story.types';

interface NarrativeVideoProps {
  video: VideoMetadata;
  onVideoEnded?: () => void;
}

/**
 * NarrativeVideo component
 * Plays a video during a specific narrative with support for delays and effects
 */
export function NarrativeVideo({ video, onVideoEnded }: NarrativeVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [shouldAutoPlay, setShouldAutoPlay] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  useEffect(() => {
    console.log('[NarrativeVideo] Component mounted with video:', video);
    console.log('[NarrativeVideo] Video URL:', video.url);
    const startDelay = video.startDelay ?? 0;

    // Show the video container after delay
    const showTimer = setTimeout(() => {
      console.log('[NarrativeVideo] Setting visible after delay:', startDelay);
      setIsVisible(true);

      // Mark that we should autoplay once the video loads
      if (video.autoPlay !== false) {
        setShouldAutoPlay(true);
      }
    }, startDelay);

    return () => {
      clearTimeout(showTimer);
    };
  }, [video.startDelay, video.autoPlay, video.url]);

  // Handle video loaded - play it if autoPlay is enabled
  const handleVideoLoaded = () => {
    console.log('[NarrativeVideo] Video loaded successfully');

    if (videoRef.current) {
      // Set volume
      videoRef.current.volume = video.volume ?? 1.0;

      if (shouldAutoPlay) {
        console.log('[NarrativeVideo] AutoPlay enabled, calling play()');
        videoRef.current.play().catch((error) => {
          console.error('[NarrativeVideo] Failed to play video:', error);
        });
        setHasStarted(true);
      }
    }
  };

  // Handle fade effects
  useEffect(() => {
    if (!videoRef.current || !hasStarted) return;

    const videoElement = videoRef.current;
    const fadeInDuration = video.fadeInDuration ?? 0;

    if (fadeInDuration > 0) {
      // Start with low opacity and fade in
      videoElement.style.transition = `opacity ${fadeInDuration}ms ease-in-out`;
      videoElement.style.opacity = '0';

      // Trigger fade in
      setTimeout(() => {
        videoElement.style.opacity = (video.opacity ?? 1.0).toString();
      }, 10);
    }
  }, [hasStarted, video.fadeInDuration, video.opacity]);

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: isFadingOut ? 0 : 1 }}
          exit={{ opacity: 0 }}
          transition={{
            duration: isFadingOut ? (video.fadeOutDuration ?? 500) / 1000 : (video.fadeInDuration ?? 500) / 1000
          }}
          className="absolute inset-0 z-50 pointer-events-none bg-transparent flex items-center justify-center"
        >
          <video
            ref={videoRef}
            src={video.url}
            className="max-w-full max-h-full object-contain"
            autoPlay={video.autoPlay ?? false}
            loop={video.loop ?? false}
            muted={video.muted ?? false}
            playsInline
            preload="auto"
            style={{
              opacity: video.opacity ?? 1.0,
              backgroundColor: 'transparent',
            }}
            onLoadedData={handleVideoLoaded}
            onPlay={() => console.log('[NarrativeVideo] Video started playing')}
            onPause={() => console.log('[NarrativeVideo] Video paused')}
            onEnded={() => {
              console.log('[NarrativeVideo] Video ended');

              // Start fade out
              const fadeOutDuration = video.fadeOutDuration ?? 500; // Default 500ms fade
              setIsFadingOut(true);

              // After fade completes, hide the video entirely
              setTimeout(() => {
                setIsVisible(false);
                onVideoEnded?.();
              }, fadeOutDuration);
            }}
            onError={(e) => {
              console.error('[NarrativeVideo] Video error:', e);
              console.error('[NarrativeVideo] Failed to load video URL:', video.url);
              console.error('[NarrativeVideo] Video element error code:', videoRef.current?.error?.code);
              console.error('[NarrativeVideo] Video element error message:', videoRef.current?.error?.message);
            }}
          />
        </motion.div>
      )}
    </AnimatePresence>
  );
}
